import { useState } from "react";
import { deleteDocument, UploadFileMetadata } from "../services/apiService";
import { toast } from "react-hot-toast";
import { getUserFromToken } from "../services/authService";
import ConfirmationDialog from "./ConfirmationDialog";
 

function JobDescription({ activeTab, selectedRequest, isEditMode, onDelete, onUpload, currentDocument, processtype }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localFile, setLocalFile] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  
  // Confirmation dialog states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: "",
    message: "",
    type: "delete",
    onConfirm: null,
  });

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  if (activeTab !== "jd") return null;

  const doc = currentDocument || selectedRequest.documents;

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    const validTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg"
    ];
    
    if (!file) {
      toast.error("No file selected.");
      return;
    }

    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Only PDF or image files are allowed.");
      e.target.value = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error("File size exceeds 5MB. Please upload a smaller file.");
      e.target.value = null;
      return;
    }

    const username = getUserFromToken();
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result.split(",")[1];

      const fileData = {
        fileName: file.name,
        fileBase64: base64String,
        size: file.size,
        mimeType: file.type,
        reference_id: selectedRequest.request_id || 0,
        process_type: processtype, // "JobDescription",
        uploaded_by: username,
        uploaded_at: new Date().toISOString(),
        IsActive: true,
      };

      setLocalFile(fileData);
      setIsSaved(false);
    };
    reader.readAsDataURL(file);
    e.target.value = null;
  };

  // Updated delete handler with confirmation dialog
  const handleDelete = () => {
    setConfirmConfig({
      title: "Delete Document",
      message: `Are you sure you want to delete "${doc?.fileName || localFile?.fileName}"? This action cannot be undone.`,
      type: "delete",
      onConfirm: performDelete,
    });
    setShowConfirmDialog(true);
  };

  const performDelete = async () => {
    try {
      setIsDeleting(true);
      
      const result = await deleteDocument(selectedRequest.request_id);

      if (result.message === "Document deleted successfully") {
        toast.success("Document deleted successfully!");
        onDelete?.(doc);
        setLocalFile(null);
        setIsSaved(false);
        if (selectedRequest.documents) {
          selectedRequest.documents = null;
        }
      } else {
        toast.error("Failed to delete document.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting the document.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!localFile) return toast.warning("No file selected to save.");

    try {
      setIsSaving(true);
      const result = await UploadFileMetadata(localFile);
      toast.success("Document saved successfully!");

      setIsSaved(true);
      onUpload?.(localFile);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save document.");
    } finally {
      setIsSaving(false);
    }
  };

  const displayDoc = localFile || doc;

  return (
    <div className="">
      <label className="text-gray-500 font-medium">Attached Document</label>

      {displayDoc ? (
        <div className="text-primary-800 mt-1">
          <p>
            <strong>File Name:</strong> {displayDoc.fileName}
          </p>

          {displayDoc.fileBase64 ? (
            <>
              <div className="flex items-center flex-wrap gap-3 mt-2">
                <button
                  onClick={openModal}
                  className="text-primary-800 bg-primary-800/10 px-3 py-1 rounded-md hover:bg-primary-800 hover:text-white border border-primary-400 transition-colors"
                >
                  View Document
                </button>

                <a
                  href={`data:${displayDoc.mimeType};base64,${displayDoc.fileBase64}`}
                  download={displayDoc.fileName}
                  className="text-primary-800 bg-primary-800/10 px-3 py-1 rounded-md hover:bg-primary-800 hover:text-white border border-primary-400 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download
                </a>

                {isEditMode && (doc || (localFile && isSaved)) && (
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className={`px-3 py-1 rounded-md border transition-colors duration-200 ${
                      isDeleting
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
                    }`}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                )}

                {isEditMode && localFile && !isSaved && (
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={`px-3 py-1 rounded-md transition-colors duration-200 ${
                      isSaving
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "text-white bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                )}
              </div>

              {isModalOpen && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                  onClick={closeModal}
                >
                  <div
                    className="bg-white p-4 w-full md:w-2/3 lg:w-1/2 rounded-lg shadow-md"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-semibold">{displayDoc.fileName}</h2>
                      <button
                        onClick={closeModal}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        ✕
                      </button>
                    </div>

                    {displayDoc.mimeType.startsWith("image/") && (
                      <img
                        src={`data:${displayDoc.mimeType};base64,${displayDoc.fileBase64}`}
                        alt={displayDoc.fileName}
                        width="100%"
                        height="500px"
                        className="max-w-full max-h-[70vh] object-contain"
                      />
                    )}

                    {displayDoc.mimeType === "application/pdf" && (
                      <iframe
                        src={`data:${displayDoc.mimeType};base64,${displayDoc.fileBase64}`}
                        title={displayDoc.fileName}
                        width="100%"
                        height="500px"
                        className="border"
                      />
                    )}

                    {displayDoc.mimeType.startsWith("text/") && (
                      <pre className="whitespace-pre-wrap text-sm max-h-[70vh] overflow-auto bg-gray-100 p-2 rounded">
                        {atob(displayDoc.fileBase64)}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p>File details not available for download.</p>
          )}
        </div>
      ) : (
        <>
          <p className="text-orange-500">No attached document available.</p>
        </>
      )}

      {/* Upload New - only if no document exists */}
      {isEditMode && (!doc && (!localFile || !isSaved)) && (
        <label
          htmlFor="upload-new-file"
          className="cursor-pointer text-white px-3 py-1 rounded-md bg-primary-800 hover:bg-blue-700 transition-colors duration-200 mt-3 inline-block"
        >
          Upload New
        </label>
      )}

      <input
        type="file"
        id="upload-new-file"
        className="hidden"
        onChange={handleFileChange}
        accept="*/*"
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setConfirmConfig({
            title: "",
            message: "",
            type: "delete",
            onConfirm: null,
          });
        }}
        onConfirm={() => {
          if (confirmConfig.onConfirm) {
            confirmConfig.onConfirm();
          }
        }}
        title={confirmConfig.title}
        message={confirmConfig.message}
        type={confirmConfig.type}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}

export default JobDescription;